import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { Form, FormikProvider, getIn, useFormik } from 'formik';
import useSettings from 'hooks/useSettings';
import React, { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { showMessage } from 'utils/snackbar';
import { BoostService } from '_apis_/boost';
import { IFormAccordion, IFormDependentField, IFormField, ISelectOption, MFormProps } from '.';
import MAccordion from '../MAccordion';
import MFormAutocomplete from './MFormAutocomplete';
import MFormMultiselect from './MFormMultiselect';
import MFormSwitch from './MFormSwitch';
import MFormText from './MFormText';

function MForm({
  id,
  heading,
  isEdit,
  fields,
  additionalFields,
  additionalFieldsValid = true,
  accordions,
  nonFormAccordions,
  model,
  initialValues,
  additionalValues,
  validationSchema,
  apiMethod,
  fullWidthButtons = false,
  submitButtonName = 'Create',
  apiUrl,
  successMessage,
  submitAction,
  dialogContent,
  dialogButtonAction,
  formTouched = false,
  additionalBody,
  additionalButtons,
  enableCancel,
  enableRemove = false,
  links,
  cardLayout = true,
  containerLayout = true,
  removeAction = () => {},
  cancelAction = () => {},
  onSuccessAction = () => {}
}: MFormProps) {
  const [boostFormTouched, setBoostFormTouched] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<string | false>(`panel0`);

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleClose = () => setDialogOpen(false);

  const boostService = new BoostService();
  const theme = useTheme();
  const { themeStretch } = useSettings();

  useEffect(() => {
    setBoostFormTouched(formTouched);
  }, [formTouched]);

  const changeSwitchValues = (fields: IFormField[] | undefined, values: any) => {
    fields
      ?.filter((item: IFormField) => item.switch && item.switchValueType === 'number')
      .forEach((element) => {
        values[element.name] = values[element.name] === true ? 1 : 0;
      });
  };

  const formSubmitAction = async (body: any) => {
    try {
      const response = await boostService.submitForm(apiMethod, body, apiUrl);
      if (response && response.status >= 200 && response.status <= 299) {
        if (successMessage) {
          showMessage(successMessage, 'success');
        }
        cancelAction();
        onSuccessAction(response);
      }
    } catch (error) {
      if (error.data) {
        const msg = error?.data?.Result
          ? error.data.Result
          : error?.data?.message
          ? error.data.message
          : 'Error Creating the form';
        showMessage(msg, 'error');
      } else {
        showMessage('Error Creating the form', 'error');
      }
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        changeSwitchValues(fields, values);
        const allValues = { ...values, ...additionalValues };
        const body = model
          ? {
              [model]: allValues,
              ...(additionalBody && additionalBody)
            }
          : allValues;
        if (submitAction) {
          submitAction(allValues);
        } else {
          formSubmitAction(body);
        }
      } catch (error) {
        setErrors(error);
      } finally {
        setSubmitting(false);
      }
    }
  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setValues,
    resetForm,
    setFieldTouched
  } = formik;

  const getFields = (fields: any[], accordionTitle?: string) => {
    const key = accordionTitle ? `${accordionTitle}-${id}` : id;
    return (
      <Grid item key={`grid-item-${key}`} xs={12}>
        <Stack key={`grid-item-stack-${key}`} sx={{ py: 3 }} spacing={3}>
          {fields?.map((item, index) => {
            if (item.fields) {
              return (
                <Grid container key={`grid-item-child-${key}${index}`} spacing={2}>
                  {item.fields.map((eachField, index) => {
                    return (
                      <Grid key={`grid-item-child-stack-${key}${index}`} item xs={6}>
                        {getField(eachField)}
                      </Grid>
                    );
                  })}
                </Grid>
              );
            } else {
              return !item.hidden && (item.type === 'table' ? item.table : getField(item));
            }
          })}
          {additionalFields?.map((item) => item)}
        </Stack>
      </Grid>
    );
  };

  const getField = (item: IFormField) => {
    const { multiple, ...rest } = item;

    const { name } = rest;
    const props = {
      item: rest,
      t: getIn(touched, name),
      e: getIn(errors, name),
      fieldProps: { ...getFieldProps(name) },
      fieldCallback
    };

    return item.switch ? (
      <MFormSwitch
        item={rest}
        key={`mui-switch-${name}`}
        values={values}
        switchCallback={switchCallback}
      />
    ) : item.options && item.options.length > 0 ? (
      multiple ? (
        <MFormMultiselect key={`mui-autocomplete-${name}`} {...props} />
      ) : (
        <MFormAutocomplete key={`mui-autocomplete-${name}`} {...props} />
      )
    ) : item.heading ? (
      <Typography key={`mui-text-${name}`} variant="h6">
        {item.label}
      </Typography>
    ) : item.description ? (
      <Typography
        key={`mui-text-${name}`}
        variant="body1"
        style={{
          paddingLeft: '15px'
        }}
      >
        {item.label}
      </Typography>
    ) : item.empty ? (
      <></>
    ) : (
      <MFormText key={`mui-text-${name}`} {...props} />
    );
  };

  const switchCallback = (item: IFormField, checked: boolean) => {
    const { name, dependentFields } = item;
    changeFieldValue(name, checked, dependentFields);
  };

  const processFieldValue = (
    item: IFormField,
    newValue: ISelectOption | ISelectOption[] | string
  ) => {
    const { name, type, fixed = 0, fixedNumber = 0, dependentFields } = item;
    if (Array.isArray(newValue)) {
      const selected = newValue.map((nv) => nv.value);
      changeFieldValue(name, selected, dependentFields);
    } else {
      const value: any = newValue ? (typeof newValue === 'object' ? newValue.value : newValue) : '';
      let finalValue = value;
      // limit numbers if it needs to be fixed
      if (fixed && type === `number`) {
        const splitNumber = value.toString().split('.');
        if (splitNumber[1]) {
          const numOfDecimals = splitNumber[1].length;
          if (numOfDecimals > fixed) {
            finalValue = value.toString();
            finalValue = finalValue.substring(0, finalValue.length - 1);
          }
        }
      } else if ((fixed || fixedNumber) && type === `percentage`) {
        const splitNumber = value.toString().split('.');
        // let numOfDecimals = 0
        if (splitNumber[0]) {
          const numberLength = splitNumber[0].length;
          let wholeNumberDigit = fixedNumber;
          if (splitNumber[1] > 0 || splitNumber[0] > 100) {
            wholeNumberDigit = 2;
          }
          if (numberLength > wholeNumberDigit) {
            splitNumber[0] = splitNumber[0].substring(0, splitNumber[0].length - 1);
            finalValue = splitNumber[0] + (splitNumber[1] ? '.' + splitNumber[1] : '');
          }
        }
        if (splitNumber[1]) {
          const numOfDecimals = splitNumber[1].length;
          if (numOfDecimals > fixed) {
            finalValue = value.toString();
            finalValue = finalValue.substring(0, finalValue.length - 1);
          }
        }
      }
      changeFieldValue(name, finalValue, dependentFields);
    }
  };

  const fieldCallback = (item: IFormField, newValue: ISelectOption | ISelectOption[] | string) => {
    const { openDialogOnChange } = item;
    if (openDialogOnChange && newValue) {
      setDialogOpen(true);
    } else {
      processFieldValue(item, newValue);
    }
    return formik;
  };

  const setArrayObjectValue = (name: string, newValue: any, values: any) => {
    //do not use lookahead and lookbehind as they are not supported for Safari
    const matches = name.match(/(\[(.*?)\])/g);
    if (matches && matches.length > 0) {
      matches.forEach((m, i) => (matches[i] = m.replace(/[[\]]/g, '')));
      if (matches.length === 2) {
        values[matches[0]][matches[1]] = newValue;
      } else {
        values[matches[0]][Number(matches[1])][matches[2]] = newValue;
      }
    }
  };

  const setObjectValue = (name: string, newValue: any, values: any) => {
    if (name.indexOf(`[`) > -1) {
      setArrayObjectValue(name, newValue, values);
    } else {
      values[name] = newValue;
    }
  };

  const changeFieldValue = (
    name: string,
    newValue: any,
    dependentFields?: IFormDependentField[] | undefined
  ) => {
    const newValues = { ...values };
    setObjectValue(name, newValue, newValues);
    if (dependentFields && dependentFields.length > 0) {
      dependentFields.forEach((field) => {
        const value = typeof field.value === 'function' ? field.value(newValue) : field.value;
        //do not check for empty string condition here
        //we need to set the value of the field to empty string
        if (value != undefined) {
          setObjectValue(field.name, value, newValues);
        }
      });
    }
    flushSync(() => {
      setValues({
        ...newValues
      });
    });
    setBoostFormTouched(true);
    setFieldTouched(name, true);
  };

  const resetBoostForm = () => {
    resetForm();
    setBoostFormTouched(false);
  };

  const getHeading = () => {
    return (
      heading && (
        <Box sx={{ mb: 5, mt: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4">{heading}</Typography>
            </Box>
          </Box>
        </Box>
      )
    );
  };

  /**
   * Only enable "Submit" button if the form is touched and it is valid

   * @returns boolean
   */
  const disableSubmit = () => {
    return !(boostFormTouched || formik.dirty) || !formik.isValid || !additionalFieldsValid;
  };

  const getForm = () => {
    return (
      <React.StrictMode>
        {heading && getHeading()}
        <Dialog
          open={dialogOpen}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">{dialogContent}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                dialogButtonAction();
                setBoostFormTouched(true);
                setDialogOpen(false);
              }}
            >
              Yes
            </Button>
            <Button onClick={handleClose} autoFocus>
              No
            </Button>
          </DialogActions>
        </Dialog>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit} title={heading}>
            <Grid container key={`grid-container-${id}`} spacing={3}>
              {accordions
                ? accordions.map((accordion: IFormAccordion, i: number) => {
                    const panel = `panel${i}`;
                    return (
                      <Grid item key={`grid_accordion_${panel}`} xs={12}>
                        <MAccordion
                          sx={{ boxShadow: theme.shadows[3] }}
                          key={`accordion_panel${i}`}
                          expanded={expanded === panel}
                          onChange={handleChange(panel)}
                          title={accordion.title}
                          info={accordion.info}
                          help={accordion.help}
                        >
                          {getFields(accordion.fields)}
                        </MAccordion>
                      </Grid>
                    );
                  })
                : fields && getFields(fields)}
              {nonFormAccordions?.map((item: any, i) => {
                const panel = `non_form_panel${i}`;
                return (
                  item.content && (
                    <Grid item key={`non_form_grid_${panel}`} xs={12}>
                      <MAccordion
                        sx={{ boxShadow: theme.shadows[3] }}
                        key={`non_form_accordion_panel${i}`}
                        expanded={expanded === panel}
                        onChange={handleChange(panel)}
                        title={item.title}
                        help={item.help}
                      >
                        {expanded ? item.content : <></>}
                      </MAccordion>
                    </Grid>
                  )
                );
              })}
            </Grid>
            <Grid item key={`buttons-${id}`} xs={12}>
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  '& > *': { m: '8px !important' }
                }}
              >
                <LoadingButton
                  disabled={disableSubmit()}
                  data-testid={`submit-${id}`}
                  fullWidth={fullWidthButtons}
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                >
                  {!isEdit ? submitButtonName : 'Save'}
                </LoadingButton>
                {enableCancel && (
                  <Button
                    fullWidth={fullWidthButtons}
                    variant="contained"
                    data-testid={'cancel-' + id}
                    onClick={() => (cancelAction ? cancelAction() : resetBoostForm())}
                  >
                    Cancel
                  </Button>
                )}
                {enableRemove && (
                  <Button
                    fullWidth={fullWidthButtons}
                    variant="contained"
                    color={'error'}
                    data-testid={'remove-' + id}
                    onClick={removeAction}
                  >
                    Remove
                  </Button>
                )}
                {additionalButtons?.map((button) => button)}
              </Box>
            </Grid>
            <Grid item key={`links-${id}`} xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  '& > *': { m: '8px !important' }
                }}
              >
                {links?.map((link: any) => link)}
              </Box>
            </Grid>
          </Form>
        </FormikProvider>
      </React.StrictMode>
    );
  };

  return containerLayout ? (
    <Container maxWidth={themeStretch ? false : 'lg'}>
      {cardLayout ? <Card sx={{ p: 3 }}>{getForm()}</Card> : getForm()}
    </Container>
  ) : cardLayout ? (
    <Card sx={{ p: 3 }}>{getForm()}</Card>
  ) : (
    getForm()
  );
}

export default MForm;
